import React, { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { InputAdornment, SxProps, TextField, Theme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

import { DriverSelectOption } from "./buildDriverSelectOptions";
import { filterDriverOptions } from "./filterDriverOptions";
import DriverOptionRow from "./DriverOptionRow";
import DriverGroupRow from "./DriverGroupRow";

export const autocompleteListboxSx: SxProps<Theme> = {
  padding: 0,
  "& .MuiAutocomplete-option": {
    minHeight: 56,
    padding: 0,
    "&[aria-selected='true']": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true']:hover": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true'].Mui-focused": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true'].Mui-focusVisible": {
      backgroundColor: "transparent",
    },
  },
};

const INPUT_SX = {
  minWidth: 200,
  maxWidth: 315,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    paddingLeft: "16px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#202224",
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
    color: "#202224",
  },
};

const PAPER_SX = {
  minWidth: 315,
  borderRadius: "8px",
  marginTop: "4px",
};

interface OrderDriverAutocompleteProps {
  options: DriverSelectOption[];
  value: DriverSelectOption | null;
  showDriverInsight: boolean;
  onChange: (option: DriverSelectOption | null) => void;
  loading?: boolean;
}

function OrderDriverAutocomplete({
  options,
  value,
  showDriverInsight,
  onChange,
  loading,
}: OrderDriverAutocompleteProps) {
  const { t } = useTranslation();
  const otherLabel = t("otherDeliveryDrivers");
  const hasMapDrivers = useMemo(
    () => options.some((option) => option.groupKey === "map"),
    [options],
  );
  const mapGroupLabel = useMemo(() => {
    const mapCount = options.filter((o) => o.groupKey === "map").length;
    return mapCount === 1 ? t("driverFromThisMap") : t("driversFromThisMap");
  }, [options, t]);

  const resolveGroupLabel = (option: DriverSelectOption): string => {
    if (option.groupKey === "none") {
      return "";
    }
    return option.groupKey === "map" ? mapGroupLabel : otherLabel;
  };

  const resolveOptionLabel = (option: DriverSelectOption): string =>
    option.label;

  return (
    <Autocomplete
      disablePortal
      sx={{ width: "55%" }}
      id="order-driver-select"
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      groupBy={resolveGroupLabel}
      getOptionLabel={resolveOptionLabel}
      filterOptions={filterDriverOptions}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      ListboxProps={{ sx: autocompleteListboxSx }}
      renderGroup={(params) => (
        <DriverGroupRow
          params={params}
          otherLabel={otherLabel}
          hasMapDrivers={hasMapDrivers}
        />
      )}
      renderOption={(liProps, option) => (
        <DriverOptionRow
          liProps={liProps}
          option={option}
          showDriverInsight={showDriverInsight}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("changeDeliveryPerson")}
          placeholder={t("selectOptionPlaceholder")}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#202224", fontSize: 24 }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          sx={INPUT_SX}
        />
      )}
      componentsProps={{ paper: { sx: PAPER_SX } }}
    />
  );
}

export default OrderDriverAutocomplete;
